import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  CheckCircle2,
  ClipboardCopy,
  HeartHandshake,
  LayoutGrid,
  List,
  Map as MapIcon,
  MapPinned,
  PanelsTopLeft,
  Plus,
  Radar,
  RefreshCw,
  Search,
  SlidersHorizontal,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { PartnerDirectoryCard } from "@/components/admin/PartnerDirectoryCard";
import { PartnerDirectoryTable } from "@/components/admin/PartnerDirectoryTable";
import { AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { FriendlyIllustration } from "@/components/ui/FriendlyIllustration";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/StatCard";
import { useToast } from "@/components/ui/use-toast";
import { usePartners, type Partner } from "@/context/PartnerContext";
import type { ServiceType } from "@/context/LeadContext";
import { SERVICE_OPTIONS as CATALOG_SERVICE_OPTIONS } from "@/lib/serviceCatalog";

type StatusFilter = "all" | "active" | "inactive";
type ReadinessFilter = "all" | "ready" | "attention" | "quiet" | "sms_suppressed";
type MiniPageFilter = "all" | "complete" | "incomplete" | "missing_image" | "missing_description";
type SortKey = "volume" | "recent_volume" | "mini_priority" | "recent" | "quiet" | "name" | "newest";

const PAGE_SIZE = 12;
const formatNumber = new Intl.NumberFormat("el-GR").format;

const SERVICE_OPTIONS: Array<{ value: ServiceType | "all"; label: string }> = [
  { value: "all", label: "Όλες οι υπηρεσίες" },
  ...CATALOG_SERVICE_OPTIONS.map(({ value, label }) => ({ value, label })),
];

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "volume", label: "Περισσότερα leads" },
  { value: "recent_volume", label: "Περισσότερα στις 30 ημέρες" },
  { value: "mini_priority", label: "Προτεραιότητα mini page" },
  { value: "recent", label: "Πιο πρόσφατη δραστηριότητα" },
  { value: "quiet", label: "Μεγαλύτερη αδράνεια" },
  { value: "name", label: "Όνομα Α–Ω" },
  { value: "newest", label: "Νεότερη εγγραφή" },
];

const timestamp = (value?: string | null): number => value ? new Date(value).getTime() || 0 : 0;
const percent = (value: number, total: number): number => total > 0 ? Math.round((value / total) * 100) : 0;

const Partners: React.FC = () => {
  const {
    partners,
    loading,
    error,
    directorySummary: summary,
    serviceCoverage,
    generatedAt,
    removePartner,
    refreshPartners,
  } = usePartners();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim().toLocaleLowerCase("el-GR"));
  const [status, setStatus] = useState<StatusFilter>("active");
  const [service, setService] = useState<ServiceType | "all">("all");
  const [readiness, setReadiness] = useState<ReadinessFilter>("all");
  const [miniPage, setMiniPage] = useState<MiniPageFilter>("all");
  const [sort, setSort] = useState<SortKey>("volume");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [page, setPage] = useState(1);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRegistrationLink, setShowRegistrationLink] = useState(false);

  const routingCoverage = percent(summary.routing_ready, summary.active);
  const miniPageCoverage = percent(summary.mini_page_complete, summary.active);
  const generatedLabel = generatedAt ? formatDistanceToNow(parseISO(generatedAt), { addSuffix: true, locale: el }) : "";

  const filteredPartners = useMemo(() => {
    const result = partners.filter((partner) => {
      if (status === "active" && !partner.active) return false;
      if (status === "inactive" && partner.active) return false;
      if (service !== "all" && !partner.services.includes(service)) return false;
      if (readiness === "ready" && !partner.routingReady) return false;
      if (readiness === "attention" && (!partner.active || (partner.attentionReasons?.length || 0) === 0)) return false;
      if (readiness === "quiet" && (!partner.active || (partner.leads30 || 0) > 0)) return false;
      if (readiness === "sms_suppressed" && (!partner.active || !partner.sms_opt_out)) return false;
      if (miniPage === "complete" && !partner.miniPageComplete) return false;
      if (miniPage === "incomplete" && partner.miniPageComplete) return false;
      if (miniPage === "missing_image" && !partner.miniPageMissing?.includes("image")) return false;
      if (miniPage === "missing_description" && !partner.miniPageMissing?.includes("description")) return false;
      if (!deferredSearch) return true;
      return [partner.name, partner.location, partner.phone, partner.email]
        .some((value) => value.toLocaleLowerCase("el-GR").includes(deferredSearch));
    });

    return result.sort((a, b) => {
      if (sort === "mini_priority") {
        return Number(Boolean(a.miniPageComplete)) - Number(Boolean(b.miniPageComplete))
          || (b.leads30 || 0) - (a.leads30 || 0)
          || (b.leadCount || 0) - (a.leadCount || 0)
          || a.name.localeCompare(b.name, "el");
      }
      if (sort === "recent_volume") return (b.leads30 || 0) - (a.leads30 || 0) || a.name.localeCompare(b.name, "el");
      if (sort === "recent") return timestamp(b.lastLead) - timestamp(a.lastLead) || a.name.localeCompare(b.name, "el");
      if (sort === "quiet") return timestamp(a.lastLead) - timestamp(b.lastLead) || a.name.localeCompare(b.name, "el");
      if (sort === "name") return a.name.localeCompare(b.name, "el");
      if (sort === "newest") return timestamp(b.createdAt) - timestamp(a.createdAt) || a.name.localeCompare(b.name, "el");
      return (b.leadCount || 0) - (a.leadCount || 0) || a.name.localeCompare(b.name, "el");
    });
  }, [deferredSearch, miniPage, partners, readiness, service, sort, status]);

  const totalPages = Math.max(1, Math.ceil(filteredPartners.length / PAGE_SIZE));
  const visiblePartners = filteredPartners.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [deferredSearch, miniPage, readiness, service, sort, status, viewMode]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const resetFilters = () => {
    setSearch("");
    setStatus("active");
    setService("all");
    setReadiness("all");
    setMiniPage("all");
    setSort("volume");
  };
  const hasFilters = search !== "" || status !== "active" || service !== "all" || readiness !== "all" || miniPage !== "all" || sort !== "volume";

  const showIncompleteMiniPages = () => {
    setStatus("active");
    setMiniPage("incomplete");
    setSort("mini_priority");
  };

  const changeMiniPageFilter = (value: MiniPageFilter) => {
    setMiniPage(value);
    if (value !== "all") setSort("mini_priority");
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;
    setIsDeleting(true);
    try {
      await removePartner(partnerToDelete.id);
      toast({ title: "Ο συνεργάτης διαγράφηκε", description: `${partnerToDelete.name} αφαιρέθηκε οριστικά από το δίκτυο.` });
      setPartnerToDelete(null);
    } catch (caughtError) {
      toast({ title: "Η διαγραφή δεν ολοκληρώθηκε", description: caughtError instanceof Error ? caughtError.message : "Δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyRegistrationLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/partner/register`);
      toast({ title: "Ο σύνδεσμος αντιγράφηκε", description: "Μπορείτε να τον στείλετε στον νέο συνεργάτη." });
    } catch {
      toast({ title: "Δεν έγινε αντιγραφή", description: "Επιλέξτε τον σύνδεσμο και αντιγράψτε τον χειροκίνητα.", variant: "destructive" });
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Δίκτυο συνεργατών"
        title="Συνεργάτες"
        description="Ελέγξτε ποιοι είναι έτοιμοι να λάβουν leads, πού υπάρχουν κενά κάλυψης και ποιοι χρειάζονται ρύθμιση."
        icon={<HeartHandshake />}
        iconGradient="blue"
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Button type="button" variant="outline" className="h-11 gap-2" onClick={() => setShowRegistrationLink(true)}><UserPlus className="h-4 w-4" /> Εγγραφή</Button>
            <Button asChild variant="outline" className="h-11 gap-2"><Link to="/admin/partners/map"><MapIcon className="h-4 w-4" /> Χάρτης</Link></Button>
            <Button asChild className="col-span-2 h-11 gap-2 bg-slate-950 text-white hover:bg-slate-800 sm:col-span-1"><Link to="/admin/partners/new"><Plus className="h-4 w-4" /> Νέος συνεργάτης</Link></Button>
          </div>
        }
      />

      {loading && partners.length === 0 ? (
        <>
          <AdminMetricSkeletons count={4} />
          <Skeleton className="h-60 rounded-2xl" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-96 rounded-2xl" />)}</div>
        </>
      ) : error && partners.length === 0 ? (
        <AdminErrorState message={error} onRetry={() => void refreshPartners()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
            <StatCard title="Ενεργό δίκτυο" value={formatNumber(summary.active)} description={`${formatNumber(summary.inactive)} ανενεργοί`} icon={<Users />} variant="info" size="compact" />
            <StatCard title="Έτοιμοι για routing" value={`${routingCoverage}%`} description={`${formatNumber(summary.routing_ready)} από ${formatNumber(summary.active)} ενεργούς`} icon={<Radar />} variant={routingCoverage >= 90 ? "success" : "warning"} size="compact" />
            <StatCard title="Leads δικτύου · 30ημ." value={formatNumber(summary.leads_30d)} description={`${formatNumber(summary.total_leads)} ιστορικά ανατεθειμένα`} icon={<HeartHandshake />} variant="gradient" size="compact" />
            <StatCard title="Χρειάζονται έλεγχο" value={formatNumber(summary.needs_attention)} description={`${formatNumber(summary.quiet_30d)} χωρίς lead στις 30 ημέρες`} icon={<AlertTriangle />} variant={summary.needs_attention > 0 ? "danger" : "success"} size="compact" />
            <button
              type="button"
              onClick={showIncompleteMiniPages}
              aria-label={`Προβολή ${summary.mini_page_incomplete} ενεργών συνεργατών με ελλιπή mini page`}
              aria-pressed={miniPage === "incomplete"}
              className="col-span-2 min-w-0 rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 xl:col-span-1"
            >
              <StatCard
                title="Mini pages πλήρεις"
                value={`${formatNumber(summary.mini_page_complete)}/${formatNumber(summary.active)}`}
                description={`${formatNumber(summary.mini_page_incomplete)} χρειάζονται στοιχεία · ${miniPageCoverage}%`}
                icon={<PanelsTopLeft />}
                variant={summary.mini_page_incomplete > 0 ? "warning" : "success"}
                size="compact"
                className={miniPage === "incomplete" ? "border-sky-400 ring-2 ring-sky-100" : ""}
              />
            </button>
          </div>

          <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="grid gap-6 p-4 sm:p-5 lg:grid-cols-[minmax(250px,0.65fr)_minmax(0,1.35fr)] lg:items-center">
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div><p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Ετοιμότητα δικτύου</p><p className="mt-2 text-4xl font-bold tabular-nums tracking-tight text-slate-950">{routingCoverage}%</p></div>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${routingCoverage >= 90 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{routingCoverage >= 90 ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}</div>
                </div>
                <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${routingCoverage >= 90 ? "bg-emerald-600" : "bg-amber-500"}`} style={{ width: `${routingCoverage}%` }} /></div>
                <p className="mt-3 text-xs leading-5 text-slate-500">Έτοιμος σημαίνει ενεργός συνεργάτης με υπηρεσία, συντεταγμένες και έγκυρη ακτίνα κάλυψης.</p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-slate-50 p-3"><p className="text-slate-500">Με γεωκωδικοποίηση</p><p className="mt-1 text-lg font-bold tabular-nums text-slate-950">{summary.geocoded}</p></div>
                  <button type="button" onClick={() => setReadiness("attention")} className="min-h-11 rounded-xl bg-amber-50 p-3 text-left transition hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"><p className="text-amber-800">Σημεία προσοχής</p><p className="mt-1 text-lg font-bold tabular-nums text-amber-950">{summary.needs_attention}</p></button>
                </div>
              </div>

              <div className="min-w-0 border-t border-slate-100 pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                <div className="mb-3 flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-slate-950">Ενεργοί ανά υπηρεσία</p><p className="mt-1 text-xs text-slate-500">Πατήστε υπηρεσία για άμεσο φιλτράρισμα.</p></div><MapPinned className="h-5 w-5 text-sky-700" /></div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {serviceCoverage.map((row) => {
                    const label = SERVICE_OPTIONS.find((option) => option.value === row.service)?.label || row.service;
                    const selected = service === row.service;
                    return (
                      <button key={row.service} type="button" onClick={() => setService(selected ? "all" : row.service)} className={`flex min-h-12 items-center justify-between gap-2 rounded-xl border px-3 text-left text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 ${selected ? "border-sky-700 bg-sky-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-sky-50"}`}>
                        <span className="truncate">{label}</span><span className={`rounded-full px-2 py-1 font-bold tabular-nums ${selected ? "bg-white/15" : "bg-slate-100 text-slate-900"}`}>{row.active_partners}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div><CardTitle className="flex items-center gap-2 text-base text-slate-950"><SlidersHorizontal className="h-4 w-4 text-sky-700" /> Φίλτρα δικτύου</CardTitle><CardDescription className="mt-1">Αναζήτηση, ετοιμότητα και πραγματική δραστηριότητα.</CardDescription></div>
                {hasFilters && <Button type="button" variant="ghost" className="h-11 shrink-0 gap-1.5 text-slate-600" onClick={resetFilters}><X className="h-4 w-4" /><span className="hidden sm:inline">Καθαρισμός</span></Button>}
              </div>
            </CardHeader>
            <CardContent className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3 2xl:grid-cols-4">
              <div className="relative sm:col-span-2 xl:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Όνομα, περιοχή, τηλέφωνο ή email" aria-label="Αναζήτηση συνεργάτη" className="h-11 pl-9 pr-11" />
                {search && <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-11 w-11" onClick={() => setSearch("")} aria-label="Καθαρισμός αναζήτησης"><X className="h-4 w-4" /></Button>}
              </div>
              <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}><SelectTrigger className="h-11" aria-label="Κατάσταση συνεργάτη"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Ενεργοί</SelectItem><SelectItem value="inactive">Ανενεργοί</SelectItem><SelectItem value="all">Όλοι</SelectItem></SelectContent></Select>
              <Select value={service} onValueChange={(value) => setService(value as ServiceType | "all")}><SelectTrigger className="h-11" aria-label="Υπηρεσία συνεργάτη"><SelectValue /></SelectTrigger><SelectContent>{SERVICE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select>
              <Select value={readiness} onValueChange={(value) => setReadiness(value as ReadinessFilter)}><SelectTrigger className="h-11" aria-label="Ετοιμότητα συνεργάτη"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Κάθε ετοιμότητα</SelectItem><SelectItem value="ready">Έτοιμοι για routing</SelectItem><SelectItem value="attention">Χρειάζονται έλεγχο</SelectItem><SelectItem value="quiet">Χωρίς lead 30 ημερών</SelectItem><SelectItem value="sms_suppressed">Χωρίς SMS</SelectItem></SelectContent></Select>
              <Select value={miniPage} onValueChange={(value) => changeMiniPageFilter(value as MiniPageFilter)}><SelectTrigger className="h-11" aria-label="Πληρότητα mini page"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Κάθε mini page</SelectItem><SelectItem value="complete">Πλήρεις mini pages</SelectItem><SelectItem value="incomplete">Ελλιπείς mini pages</SelectItem><SelectItem value="missing_image">Χωρίς εικόνα</SelectItem><SelectItem value="missing_description">Χωρίς περιγραφή</SelectItem></SelectContent></Select>
              <Select value={sort} onValueChange={(value) => setSort(value as SortKey)}><SelectTrigger className="h-11" aria-label="Ταξινόμηση συνεργατών"><ArrowUpDown className="mr-2 h-4 w-4 text-slate-400" /><SelectValue /></SelectTrigger><SelectContent>{SORT_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select>
              <div className="flex h-11 rounded-xl bg-slate-100 p-1" aria-label="Τρόπος προβολής">
                <Button type="button" variant="ghost" size="icon" className={`h-9 flex-1 rounded-lg ${viewMode === "list" ? "bg-white text-sky-800 shadow-sm" : "text-slate-500"}`} onClick={() => setViewMode("list")} aria-label="Προβολή λίστας"><List className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className={`h-9 flex-1 rounded-lg ${viewMode === "grid" ? "bg-white text-sky-800 shadow-sm" : "text-slate-500"}`} onClick={() => setViewMode("grid")} aria-label="Προβολή καρτών"><LayoutGrid className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>

          <section aria-labelledby="partner-results-heading" className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div><h2 id="partner-results-heading" className="text-lg font-bold text-slate-950">Κατάλογος συνεργατών</h2><p className="mt-1 text-sm text-slate-500">{formatNumber(filteredPartners.length)} αποτελέσματα · {generatedLabel ? `ενημέρωση ${generatedLabel}` : "τρέχον snapshot"}</p></div>
              {loading && <Badge variant="outline" className="w-fit border-sky-200 bg-sky-50 text-sky-700"><RefreshCw className="mr-1 h-3 w-3 animate-spin" /> Ανανέωση</Badge>}
            </div>

            {filteredPartners.length === 0 ? (
              <div className="flex min-h-72 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center">
                <FriendlyIllustration kind="partner-network" className="h-36 w-auto max-w-[240px] drop-shadow-[0_12px_20px_rgba(14,116,190,0.12)]" /><p className="mt-2 font-semibold text-slate-800">Δεν βρέθηκαν συνεργάτες</p><p className="mt-1 max-w-md text-sm leading-6 text-slate-500">Αλλάξτε αναζήτηση, υπηρεσία, ετοιμότητα ή πληρότητα mini page για να δείτε διαφορετικό τμήμα του δικτύου.</p><Button type="button" variant="outline" className="mt-4 h-11" onClick={resetFilters}>Καθαρισμός φίλτρων</Button>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{visiblePartners.map((partner) => <PartnerDirectoryCard key={partner.id} partner={partner} onDelete={setPartnerToDelete} />)}</div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:hidden">{visiblePartners.map((partner) => <PartnerDirectoryCard key={partner.id} partner={partner} onDelete={setPartnerToDelete} />)}</div>
                <PartnerDirectoryTable partners={visiblePartners} onDelete={setPartnerToDelete} />
              </>
            )}

            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 sm:flex-row">
                <p className="text-xs text-slate-500">{formatNumber((page - 1) * PAGE_SIZE + 1)}–{formatNumber(Math.min(page * PAGE_SIZE, filteredPartners.length))} από {formatNumber(filteredPartners.length)}</p>
                <div className="flex items-center gap-2"><Button type="button" variant="outline" size="icon" className="h-11 w-11" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))} aria-label="Προηγούμενη σελίδα"><ArrowLeft className="h-4 w-4" /></Button><span className="min-w-24 text-center text-xs font-medium text-slate-600">Σελίδα {page} / {totalPages}</span><Button type="button" variant="outline" size="icon" className="h-11 w-11" disabled={page === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))} aria-label="Επόμενη σελίδα"><ArrowRight className="h-4 w-4" /></Button></div>
              </div>
            )}
          </section>
        </>
      )}

      <AlertDialog open={Boolean(partnerToDelete)} onOpenChange={(open) => !open && setPartnerToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Οριστική διαγραφή συνεργάτη;</AlertDialogTitle><AlertDialogDescription>Ο συνεργάτης <strong>{partnerToDelete?.name}</strong> δεν έχει συνδεδεμένα leads και μπορεί να διαγραφεί. Η ενέργεια δεν αναιρείται. Αν θέλετε απλώς να σταματήσει να λαμβάνει leads, επιλέξτε «Ανενεργός» από την επεξεργασία.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={isDeleting}>Άκυρο</AlertDialogCancel><AlertDialogAction onClick={handleDeletePartner} disabled={isDeleting} className="bg-rose-700 text-white hover:bg-rose-800">{isDeleting ? "Διαγραφή…" : "Οριστική διαγραφή"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showRegistrationLink} onOpenChange={setShowRegistrationLink}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Εγγραφή νέου συνεργάτη</DialogTitle><DialogDescription>Ο συνεργάτης ανοίγει τη φόρμα και χρησιμοποιεί τον κωδικό εγγραφής που διαχειρίζεστε από τις Ρυθμίσεις.</DialogDescription></DialogHeader>
          <div className="space-y-3"><div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">Ο σύνδεσμος μόνος του δεν ολοκληρώνει εγγραφή χωρίς τον ενεργό κωδικό.</div><div className="flex gap-2"><Input readOnly value={`${window.location.origin}/partner/register`} className="h-11 bg-slate-50" aria-label="Σύνδεσμος εγγραφής" /><Button type="button" size="icon" className="h-11 w-11 shrink-0" onClick={() => void copyRegistrationLink()} aria-label="Αντιγραφή συνδέσμου"><ClipboardCopy className="h-4 w-4" /></Button></div></div>
          <DialogFooter><DialogClose asChild><Button variant="outline" className="h-11">Κλείσιμο</Button></DialogClose></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
